// ClipRadar website — feature showcase v2: the same clickable auto-advancing
// cards + live demo engine as hero.tsx's FeatureShowcase (kept there for
// instant rollback — tag showcase-v1), with the final polish layer: GSAP
// scroll entrance, eased scene hand-offs, product-shot tilt on the demo and
// card micro-interactions. The demo SCENES are untouched — they render inside
// exactly as before.
import { useEffect, useLayoutEffect, useRef, useState, type ComponentType } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CRWIcon } from '../components/icons';
import { SectionHead } from '../components/primitives';
import { SCENES } from '../components/scenes';
import { useIsMobile } from '../components/hooks';
import type { ShowcaseItem } from './hero';
import '../styles/showcase.css';

gsap.registerPlugin(ScrollTrigger);

// The Google Fonts CSS loads async (index.html media=print swap) so it can land
// AFTER window load — the font swap then shifts layout and every ScrollTrigger
// start position measured pre-swap goes stale (sections' entrance tweens never
// fired and stayed at opacity 0 — bit us live 2026-07-12, mobile especially).
// GSAP's own guidance: refresh once webfonts are done. Global, so this one call
// fixes all ScrollTrigger users in the bundle (hero-v2, social, works, here).
if (typeof document !== 'undefined' && document.fonts?.ready) {
  document.fonts.ready.then(() => ScrollTrigger.refresh());
}

const DURATION = 7000;

export function FeatureShowcase({ id, label, title, sub, items, flip }: { id: string; label: string; title: string; sub: string; items: ShowcaseItem[]; flip?: boolean }) {
  const [active, setActive] = useState(0);
  const [cycle, setCycle] = useState(0); // bump to replay scene
  const [shown, setShown] = useState(0); // scene layer currently in front
  const [prev, setPrev] = useState<number | null>(null); // outgoing layer during a crossfade
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<HTMLElement | null>(null);
  const demoRef = useRef<HTMLDivElement | null>(null);
  const incomingRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  // mobile = chips-above-scene: selector, live demo and caption share one viewport
  const mobile = useIsMobile();
  const chipsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    if (boxRef.current) io.observe(boxRef.current);
    return () => io.disconnect();
  }, []);

  // replay the active scene when the section first scrolls into view
  const seen = useRef(false);
  useEffect(() => {
    if (inView && !seen.current) { seen.current = true; setCycle((c) => c + 1); }
  }, [inView]);

  useEffect(() => {
    if (!inView) return;
    timer.current = setTimeout(() => {
      setActive((a) => (a + 1) % items.length);
      setCycle((c) => c + 1);
    }, DURATION);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, cycle, inView]);

  // entrance: head, cards/chips and demo arrive together, once
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        scrollTrigger: { trigger: boxRef.current, start: 'top 72%', once: true },
      });
      if (mobile) {
        tl.from('.crws-chip', { opacity: 0, y: 10, duration: 0.5, stagger: 0.06, clearProps: 'transform' }, 0)
          .from('.crws-demo', { opacity: 0, y: 26, duration: 0.75 }, 0.1);
      } else {
        tl.from('.crws-card', { opacity: 0, x: flip ? 26 : -26, duration: 0.65, stagger: 0.09 }, 0)
          .from('.crws-demo', { opacity: 0, y: 34, duration: 0.85 }, 0.12);
      }
    }, boxRef);
    return () => ctx.revert();
  }, [flip, mobile]);

  // mobile: keep the active chip centered in its scroll row (container scroll
  // only — scrollIntoView could yank the page vertically mid-read)
  useEffect(() => {
    if (!mobile) return;
    const row = chipsRef.current;
    const chip = row?.children[active] as HTMLElement | undefined;
    if (!row || !chip) return;
    const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [active, mobile]);

  // scene switch = a true CROSSFADE. The outgoing mockup stays solid on screen
  // (settled, so it doesn't re-animate) while the incoming one materializes over
  // it — opacity + a whisper of blur, no scale, no slide — then the outgoing is
  // dropped once fully covered. No dark gap between scenes, no crude pop.
  useLayoutEffect(() => {
    if (active === shown) return;
    setPrev(shown);
    setShown(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  useLayoutEffect(() => {
    if (prev == null) return;
    const inc = incomingRef.current;
    if (!inc || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setPrev(null); return; }
    let killed = false;
    gsap.fromTo(inc, { opacity: 0, filter: 'blur(6px)' }, { opacity: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power2.out', overwrite: 'auto', onComplete: () => { if (!killed) setPrev(null); } });
    return () => { killed = true; };
  }, [prev, shown]);

  // product-shot tilt: rests at a whisper of perspective, flattens near the cursor.
  // Gated by inView so the rAF loop stops once the section scrolls off screen.
  useEffect(() => {
    if (!inView) return;
    const box = boxRef.current, demo = demoRef.current;
    if (!box || !demo) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    let raf = 0;
    let tx = 0, cx = 0;
    const rest = flip ? 4 : -4; // tilt toward the cards
    const onMove = (e: PointerEvent) => {
      const r = demo.getBoundingClientRect();
      const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
      tx = Math.max(0, Math.min(1, 1.2 - d / (r.width * 0.85)));
    };
    const tick = () => {
      cx += (tx - cx) * 0.08;
      demo.style.transform = `perspective(1600px) rotateY(${(rest * (1 - cx)).toFixed(2)}deg)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    box.addEventListener('pointermove', onMove);
    return () => { cancelAnimationFrame(raf); box.removeEventListener('pointermove', onMove); };
  }, [flip, inView]);

  const pick = (i: number) => { if (timer.current) clearTimeout(timer.current); setActive(i); setCycle((c) => c + 1); };
  const sceneAt = (i: number) => { const S: ComponentType = SCENES[items[i].scene]; return <S />; };

  // S1 verdict (founder, 2026-07-16): quiet cards — boxes stay, chrome neutral,
  // the warm timer burn is the one warm element. Icons stay CRWIcon in both states.
  const cards = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, i) => {
        const on = i === active;
        return (
          <button key={i} className={`crws-card${on ? ' crws-card-on' : ''}`} onClick={() => pick(i)}>
            {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
            <span className="crws-card-rail" aria-hidden="true"></span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: on ? 7 : 0 }}>
              <span className="crws-card-icon"><CRWIcon d={it.icon} size={15} /></span>
              <b className="crws-card-title">{it.title}</b>
              <i className="crws-card-num">0{i + 1}</i>
            </div>
            <p className="crws-card-text" style={{ maxHeight: on ? 80 : 0, opacity: on ? 1 : 0 }}>{it.text}</p>
          </button>
        );
      })}
    </div>
  );

  const demo = (
    <div className="crws-demo" ref={demoRef} style={{ minWidth: 0, height: mobile ? 'min(460px, 124vw)' : 510, position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: '-70px -90px', background: 'radial-gradient(ellipse 55% 55% at 50% 45%, rgba(var(--light-rgb),0.10), transparent 70%)', pointerEvents: 'none' }}></div>
      {prev != null ? (
        <div key={`prev-${prev}`} data-settled="true" style={{ position: 'absolute', inset: 0 }}>{sceneAt(prev)}</div>
      ) : null}
      <div key={`${shown}-${cycle}`} ref={incomingRef} style={{ position: 'absolute', inset: 0 }}>{sceneAt(shown)}</div>
    </div>
  );

  // mobile: chip selector directly above the scene, the active card's text as a
  // crossfading caption below it — tap a chip and the change happens where
  // you're already looking, no scroll round-trips.
  if (mobile) {
    return (
      <section id={id} ref={boxRef} style={{ padding: '56px 0' }}>
        <div style={{ padding: '0 24px' }}>
          <SectionHead label={label} title={title} sub={sub} />
        </div>
        <div className="crws-chips" ref={chipsRef} role="tablist" aria-label={label}>
          {items.map((it, i) => {
            const on = i === active;
            return (
              <button key={i} className={`crws-chip${on ? ' crws-chip-on' : ''}`} role="tab" aria-selected={on} onClick={() => pick(i)}>
                {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
                <span className="crws-chip-icon"><CRWIcon d={it.icon} size={13} /></span>
                {it.title}
              </button>
            );
          })}
        </div>
        <div style={{ padding: '0 20px' }}>{demo}</div>
        <p key={active} className="crw-fade crws-mobiletext">{items[active].text}</p>
      </section>
    );
  }

  return (
    <section id={id} ref={boxRef} style={{ padding: '90px 24px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead label={label} title={title} sub={sub} />
        <div className="crw-showcase-grid" style={{ display: 'grid', gridTemplateColumns: flip ? '1.35fr 1fr' : '1fr 1.35fr', gap: 36, alignItems: 'center' }}>
          {flip ? demo : cards}
          {flip ? cards : demo}
        </div>
      </div>
    </section>
  );
}
